import React from 'react';
import { Alert, Button, ConfirmDialog, TextInput } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { num } from '../../lib/aiPricingView';
import { isIrtCredit } from '../../lib/format';

/**
 * «فعال‌سازی…» (flow F8).
 *
 * The summary names what is about to change — N priced, M dearer, K cheaper,
 * J negative — and when J > 0 the confirm needs the word «تأیید» typed, the
 * double-confirm the downgrade dialog uses: an operator is about to make a
 * service lose money on every run and should have to say so.
 *
 * On a `BUSINESS_RULE` conflict (another operator activated first) the dialog
 * keeps its state, shows the reason and offers a reload; it never closes on a
 * failure (§8.1).
 */
export default function ActivateDialog({
  open, onOpenChange, detail, pending, error, conflict, onConfirm, onReload,
  // The editor's sentence when a credit priced in another currency than toman
  // (PM-0211) has changed price in the wallet since this policy was computed.
  creditChange = null,
}) {
  const { t, formatNumber } = useTranslation();
  const [typed, setTyped] = React.useState('');
  React.useEffect(() => { if (open) setTyped(''); }, [open]);

  const policy = detail?.policy;
  const negative = detail?.negative || 0;
  const word = t('aiPricing.policies.editor.activateDialog.confirmWord');
  const needsWord = negative > 0;
  // Toman against toman only — a policy whose credit is priced in another
  // currency (PM-0211) has no toman snapshot to compare.
  const walletChanged = isIrtCredit(policy?.creditCurrency)
    && detail?.walletParsehToman != null && policy?.parsehToman != null
    && num(detail.walletParsehToman) !== num(policy.parsehToman);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('aiPricing.policies.editor.activateDialog.title', { name: policy?.name || '' })}
      description={(
        <div className="flex flex-col gap-3">
          {error ? (
            <Alert
              variant="error"
              data-testid="ai-policy-activate-error"
              action={conflict ? (
                <Button variant="outline" size="sm" onClick={onReload} data-testid="ai-policy-activate-reload">
                  {t('aiPricing.policies.editor.activateDialog.reload')}
                </Button>
              ) : undefined}
            >
              {conflict ? t('aiPricing.policies.editor.activateDialog.conflict') : error}
            </Alert>
          ) : null}
          <p data-testid="ai-policy-activate-summary">
            {t('aiPricing.policies.editor.activateDialog.summary', {
              total: detail?.prices?.length || 0,
              dearer: detail?.dearer || 0,
              cheaper: detail?.cheaper || 0,
              negative,
            })}
          </p>
          <p className="text-muted-foreground">{t('aiPricing.policies.editor.activateDialog.consequence')}</p>
          {walletChanged ? (
            <Alert variant="warning" data-testid="ai-policy-activate-wallet">
              {t('aiPricing.policies.editor.parsehChanged', {
                wallet: formatNumber(num(detail.walletParsehToman)),
                snapshot: formatNumber(num(policy.parsehToman)),
              })}
            </Alert>
          ) : creditChange ? (
            <Alert variant="warning" data-testid="ai-policy-activate-wallet">{creditChange}</Alert>
          ) : null}
          {needsWord ? (
            <>
              <Alert variant="error" data-testid="ai-policy-activate-negative">
                {t('aiPricing.policies.editor.activateDialog.negativeWarning', { count: negative, word })}
              </Alert>
              <TextInput
                label={t('aiPricing.policies.editor.activateDialog.typeToConfirm')}
                value={typed}
                onChange={(e) => setTyped(e.target?.value ?? e)}
                placeholder={word}
                data-testid="ai-policy-confirm-word"
              />
            </>
          ) : null}
        </div>
      )}
      confirmLabel={t('aiPricing.policies.editor.activateDialog.confirm')}
      cancelLabel={t('common.actions.cancel')}
      destructive={needsWord}
      loading={pending}
      confirmDisabled={needsWord && typed.trim() !== word}
      onConfirm={onConfirm}
      data-testid="ai-policy-activate-confirm"
      confirmTestId="ai-policy-activate-confirm-submit"
      cancelTestId="ai-policy-activate-confirm-cancel"
    />
  );
}
